import dynamic from 'next/dynamic';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import useMediaQuery from 'hooks/useMediaQuery';

import { addListener, removeListener } from 'lib/eventManager';
import {
  createNewToast,
  deleteToastByKey,
  findToastByKey,
  isToastsContentEqual,
} from 'lib/toast';

import { useTopbar } from 'providers/TopbarProvider';

import TOAST from 'constants/toast';

const MAX_TOASTS_ACTIVE = 4;

const Toast = dynamic(() => import('.'), { ssr: false });
const ToastContainer = dynamic(() => import('./ToastContainer'), {
  ssr: false,
});

const ToastRoot = () => {
  const [toastsState, setToastsState] = useState([]);
  const { isLg } = useMediaQuery();
  const {
    topbarDesktop: { height: desktopTopbarHeight },
    topbarMobile: { height: mobileTopbarHeight },
  } = useTopbar();

  const showToast = useCallback((event) => {
    const {
      checkForDuplicates,
      key: keyToCompare,
      ...restProps
    } = event.detail ?? {};

    setToastsState((prevState) => {
      if (prevState.length >= MAX_TOASTS_ACTIVE) {
        return prevState;
      }

      const prevToast = findToastByKey(prevState, keyToCompare);

      if (checkForDuplicates && keyToCompare && prevToast) {
        if (isToastsContentEqual(prevToast, restProps)) {
          return [
            ...deleteToastByKey(prevState, keyToCompare),
            createNewToast(keyToCompare, restProps),
          ];
        }
        return prevState;
      }

      return [...prevState, createNewToast(keyToCompare, restProps)];
    });
  }, []);

  const [topToasts, bottomToasts] = useMemo(() => {
    const topToastsState = [];
    const bottomToastsState = [];

    toastsState.forEach((toast) => {
      if (toast.props.direction === TOAST.DIRECTIONS.TOP) {
        topToastsState.push(toast);
        return;
      }

      bottomToastsState.push(toast);
    });

    return [topToastsState, bottomToastsState];
  }, [toastsState]);

  const hideToast = useCallback((toastId) => {
    const { detail } = toastId;
    if (detail === null) {
      setToastsState([]);
      return;
    }

    setToastsState((prevState) =>
      toastId ? prevState.filter(({ id }) => id !== toastId) : []
    );
  }, []);

  useEffect(() => {
    addListener(TOAST.EVENTS.SHOW_TOAST, showToast);
    addListener(TOAST.EVENTS.HIDE_ALL_TOASTS, hideToast);

    return () => {
      removeListener(TOAST.EVENTS.SHOW_TOAST, showToast);
      removeListener(TOAST.EVENTS.HIDE_ALL_TOASTS, hideToast);
    };
  }, []);

  const onToastClick = (id, onClick) => {
    onClick();
    hideToast(id);
  };

  return (
    <>
      {topToasts.length > 0 && (
        <ToastContainer
          direction="top"
          overlapOverlay={false}
          offsetTop={isLg ? desktopTopbarHeight : mobileTopbarHeight}
        >
          {topToasts.map(({ id, isOpen, props: { onClick, ...restProps } }) => (
            <Toast
              key={id}
              isOpen={isOpen}
              onClose={() => hideToast(id)}
              onClick={() => onToastClick(id, onClick)}
              {...restProps}
            />
          ))}
        </ToastContainer>
      )}
      {bottomToasts.length > 0 && (
        <ToastContainer direction="bottom">
          {bottomToasts.map(
            ({ id, isOpen, props: { onClick, ...restProps } }) => (
              <Toast
                key={id}
                isOpen={isOpen}
                onClose={() => hideToast(id)}
                onClick={() => onToastClick(id, onClick)}
                {...restProps}
              />
            )
          )}
        </ToastContainer>
      )}
    </>
  );
};

export default memo(ToastRoot);
